import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { boostApi } from '@/lib/api';

const RANGES = [7, 30, 90] as const;

export function AnalyticsPage() {
  const [days, setDays] = useState<(typeof RANGES)[number]>(30);
  const series = useQuery({
    queryKey: ['boost', 'series', days],
    queryFn: () => boostApi.timeseries(days),
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold">Analytics</h1>
          <p className="mt-1 text-text-secondary">Impressions, clicks, and going-clicks over time.</p>
        </div>
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setDays(r)}
              className={
                r === days
                  ? 'rounded-full bg-brand-600 px-4 py-1.5 text-sm font-semibold'
                  : 'rounded-full border border-border bg-surface px-4 py-1.5 text-sm text-text-secondary hover:text-text-primary'
              }
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <header className="mb-4">
          <h2 className="text-lg font-bold">Daily engagement</h2>
        </header>
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series.data ?? []} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="imp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.65} />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="clk" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EC4899" stopOpacity={0.65} />
                  <stop offset="95%" stopColor="#EC4899" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="goi" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22C55E" stopOpacity={0.65} />
                  <stop offset="95%" stopColor="#22C55E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
              <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
              <YAxis stroke="#71717A" fontSize={11} />
              <Tooltip
                contentStyle={{ background: '#171717', border: '1px solid #2A2A2D', borderRadius: 12 }}
                labelStyle={{ color: '#FAFAFA' }}
              />
              <Legend wrapperStyle={{ paddingTop: 12 }} />
              <Area
                type="monotone"
                dataKey="impressions"
                stroke="#8B5CF6"
                fill="url(#imp)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="clicks"
                stroke="#EC4899"
                fill="url(#clk)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="going_clicks"
                stroke="#22C55E"
                fill="url(#goi)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
